import { useEffect, useRef, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Link } from 'react-router-dom';
import { ExternalLink, ImageUp } from 'lucide-react';
import { Card, CardBody } from '@/components/ui/Card';
import { Input } from '@/components/ui/Input';
import { Textarea } from '@/components/ui/Textarea';
import { Toggle } from '@/components/ui/Toggle';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { useToast } from '@/hooks/useToast';
import * as api from '@/api/tienda-web.api';
import type { ColoresDeTienda, ConfigDeTienda, FuentesDeTienda } from '@/api/tienda-web.api';

/**
 * TIENDA WEB — la pantalla desde la que se maneja la vitrina pública.
 *
 * Aquí se enciende, se le pone el WhatsApp al que llegan los pedidos y se
 * cambia lo que dice y cómo se ve. Nada de eso vive en el código: es lo que
 * permite que el mismo programa le sirva a otra tienda con otra marca.
 *
 * Lo que la tienda ENSEÑA no se edita aquí: sale de Productos. Una pieza
 * aparece cuando está activa y tiene al menos una foto.
 */

const ROLES_DE_COLOR: Array<{ clave: keyof ColoresDeTienda; nombre: string; uso: string }> = [
  { clave: 'fondo', nombre: 'Fondo', uso: 'El papel de la página' },
  { clave: 'superficie', nombre: 'Superficie', uso: 'Detrás de las fotos' },
  { clave: 'realce', nombre: 'Realce', uso: 'La portada y el pie' },
  { clave: 'acento', nombre: 'Acento', uso: 'Líneas, aros y logo' },
  { clave: 'texto', nombre: 'Texto', uso: 'Lo que se lee' },
  { clave: 'tinta', nombre: 'Tinta', uso: 'Títulos y botones' },
];

const ROLES_DE_FUENTE: Array<{ clave: keyof FuentesDeTienda; nombre: string; uso: string }> = [
  { clave: 'titulares', nombre: 'Titulares', uso: 'El nombre y los títulos' },
  { clave: 'texto', nombre: 'Texto', uso: 'Párrafos y descripciones' },
  { clave: 'firma', nombre: 'Firma', uso: 'La frase en caligrafía' },
  { clave: 'etiquetas', nombre: 'Etiquetas', uso: 'Tallas, botones y datos' },
];

const enRenglones = (texto: string) =>
  texto
    .split('\n')
    .map((r) => r.trim())
    .filter(Boolean);

/**
 * Una lista que se escribe un renglón por cosa. El texto vive aparte de la
 * lista: si se limpiara en cada tecla, no se podría ni abrir un renglón nuevo.
 */
function ListaEnRenglones({
  label,
  hint,
  valor,
  alCambiar,
}: {
  label: string;
  hint: string;
  valor: string[];
  alCambiar: (lista: string[]) => void;
}) {
  const [texto, setTexto] = useState(valor.join('\n'));
  // Si la lista cambia desde fuera (al guardar o recargar), el texto la sigue.
  useEffect(() => {
    setTexto((actual) => (enRenglones(actual).join('\n') === valor.join('\n') ? actual : valor.join('\n')));
  }, [valor]);
  return (
    <Textarea
      label={label}
      hint={hint}
      rows={Math.max(3, valor.length + 1)}
      value={texto}
      onChange={(e) => {
        setTexto(e.target.value);
        alCambiar(enRenglones(e.target.value));
      }}
    />
  );
}

function SubirLogo({
  cual,
  titulo,
  ayuda,
  actual,
}: {
  cual: 'emblema' | 'isotipo';
  titulo: string;
  ayuda: string;
  actual: string | null;
}) {
  const entrada = useRef<HTMLInputElement | null>(null);
  const toast = useToast();
  const qc = useQueryClient();
  const subir = useMutation({
    mutationFn: (archivo: File) => api.subirLogo(cual, archivo),
    onSuccess: () => {
      void qc.invalidateQueries({ queryKey: ['tienda-web'] });
      toast.success('Logo guardado');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  return (
    <div className="flex items-center gap-3">
      <div className="grid h-20 w-20 shrink-0 place-items-center overflow-hidden rounded-lg border border-surface-border bg-slate-50 dark:bg-slate-800">
        {actual ? (
          <img src={actual} alt="" className="max-h-full max-w-full object-contain" />
        ) : (
          <span className="text-xs text-slate-400">Sin logo</span>
        )}
      </div>
      <div className="min-w-0 flex-1">
        <p className="text-sm font-medium text-slate-800 dark:text-slate-100">{titulo}</p>
        <p className="text-xs text-slate-500 dark:text-slate-400">{ayuda}</p>
        <input
          ref={entrada}
          type="file"
          accept="image/png,image/webp,image/jpeg"
          className="hidden"
          onChange={(e) => {
            const archivo = e.target.files?.[0];
            if (archivo) subir.mutate(archivo);
            e.target.value = '';
          }}
        />
        <Button
          variant="secondary"
          size="sm"
          className="mt-2"
          loading={subir.isPending}
          onClick={() => entrada.current?.click()}
        >
          <ImageUp className="h-4 w-4" /> {actual ? 'Cambiar' : 'Subir'}
        </Button>
      </div>
    </div>
  );
}

export default function TiendaWeb() {
  const toast = useToast();
  const qc = useQueryClient();
  const { data, isLoading, isError } = useQuery({
    queryKey: ['tienda-web'],
    queryFn: api.leerTienda,
  });

  // El formulario trabaja sobre una copia: nada llega a la tienda hasta Guardar.
  const [form, setForm] = useState<ConfigDeTienda | null>(null);
  useEffect(() => {
    if (data) setForm(data.config);
  }, [data]);

  const guardar = useMutation({
    mutationFn: (c: ConfigDeTienda) => {
      const { emblema: _e, isotipo: _i, ...cambios } = c;
      return api.guardarTienda(cambios);
    },
    onSuccess: (r) => {
      qc.setQueryData(['tienda-web'], (previo: typeof data) =>
        previo ? { ...previo, config: r.config } : previo,
      );
      toast.success(r.config.activa ? 'Guardado. La tienda está encendida.' : 'Guardado');
    },
    onError: (e: Error) => toast.error(e.message),
  });

  if (isLoading || !form) {
    return isError ? (
      <p className="text-sm text-red-600">No se pudieron leer los ajustes de la tienda.</p>
    ) : (
      <Spinner />
    );
  }

  const poner = <K extends keyof ConfigDeTienda>(clave: K, valor: ConfigDeTienda[K]) =>
    setForm((f) => (f ? { ...f, [clave]: valor } : f));
  const hayCambios = JSON.stringify(form) !== JSON.stringify(data?.config);
  const vitrina = data!.vitrina;
  const conectado = data!.numeroConectado;

  return (
    <div className="mx-auto max-w-3xl space-y-4">
      <div className="flex flex-wrap items-start gap-3">
        <div className="min-w-0 flex-1">
          <h1 className="text-2xl font-bold text-slate-900 dark:text-slate-50">Tienda web</h1>
          <p className="text-sm text-slate-500 dark:text-slate-400">
            La página pública con tus piezas. Cada pedido llega a tu WhatsApp con la pieza y la
            talla ya escritas.
          </p>
        </div>
        <a href="/tienda/" target="_blank" rel="noreferrer">
          <Button variant="secondary">
            <ExternalLink className="h-4 w-4" /> Abrir la tienda
          </Button>
        </a>
      </div>

      <Card>
        <CardBody className="flex flex-wrap items-center gap-x-4 gap-y-3">
          <Toggle
            checked={form.activa}
            onChange={(v) => poner('activa', v)}
            label="Tienda encendida"
          />
          <div className="min-w-0 flex-1">
            <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
              {form.activa ? 'Encendida' : 'Apagada'}
            </p>
            <p className="text-xs text-slate-500 dark:text-slate-400">
              {form.activa
                ? 'Cualquiera con el enlace puede verla.'
                : 'Apagada no existe: quien entre al enlace no encuentra nada.'}
            </p>
          </div>
          <p className="text-sm text-slate-600 dark:text-slate-300">
            <span className="font-semibold tabular-nums">{vitrina.visibles}</span>{' '}
            {vitrina.visibles === 1 ? 'pieza a la vista' : 'piezas a la vista'}
            {vitrina.sinFoto > 0 && (
              <span className="text-amber-700 dark:text-amber-400">
                {' '}
                · {vitrina.sinFoto} sin foto, no {vitrina.sinFoto === 1 ? 'sale' : 'salen'}
              </span>
            )}
            {vitrina.fueraPorTope > 0 && (
              <span className="text-amber-700 dark:text-amber-400">
                {' '}
                · la tienda enseña las {vitrina.tope} más nuevas: {vitrina.fueraPorTope} más
                viejas no {vitrina.fueraPorTope === 1 ? 'sale' : 'salen'}
              </span>
            )}
          </p>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Pedidos</h2>
          {/* A dónde van los pedidos DE VERDAD. El número conectado manda sobre
              el escrito: es el que atiende el agente. */}
          {conectado ? (
            <p className="rounded-lg bg-emerald-50 px-3 py-2 text-sm text-emerald-800 dark:bg-emerald-900/20 dark:text-emerald-200">
              Los pedidos de la tienda llegan al WhatsApp conectado:{' '}
              <span className="font-semibold tabular-nums">+{conectado}</span>. Lo atiende tu agente.
            </p>
          ) : (
            <p className="rounded-lg bg-amber-50 px-3 py-2 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
              Todavía no hay un WhatsApp conectado, así que los pedidos van al número escrito abajo y
              no los atiende el agente.{' '}
              <Link to="/channels" className="font-semibold underline underline-offset-2">
                Conectar en Canales
              </Link>
            </p>
          )}
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label={conectado ? 'WhatsApp de respaldo' : 'WhatsApp de los pedidos'}
              inputMode="tel"
              placeholder="18095550100"
              hint={
                conectado
                  ? 'Solo se usa si el WhatsApp conectado se cae.'
                  : 'Con el código de país, solo números.'
              }
              value={form.whatsapp}
              onChange={(e) => poner('whatsapp', e.target.value)}
            />
            <Input
              label="Instagram"
              placeholder="tumarca"
              hint="El usuario, sin @."
              value={form.instagram}
              onChange={(e) => poner('instagram', e.target.value)}
            />
            <Input
              label="Canal de WhatsApp"
              placeholder="https://whatsapp.com/channel/…"
              hint="Si la marca tiene canal de novedades. Los pedidos no entran por ahí."
              value={form.canal}
              onChange={(e) => poner('canal', e.target.value)}
            />
          </div>
          <div className="flex items-center gap-3">
            <Toggle
              checked={form.mostrarPrecios}
              onChange={(v) => poner('mostrarPrecios', v)}
              label="Mostrar precios"
            />
            <div>
              <p className="text-sm font-medium text-slate-800 dark:text-slate-100">
                Mostrar precios
              </p>
              <p className="text-xs text-slate-500 dark:text-slate-400">
                Apagado, cada pieza dice «Consultar» y lleva al WhatsApp.
              </p>
            </div>
          </div>
          <Input
            label="Entrega de lo que está en existencia"
            placeholder="Entrega en 1 a 2 días"
            hint="Sale en la ficha de las piezas que tienes. Vacío, no dice nada."
            value={form.entregaEnExistencia}
            onChange={(e) => poner('entregaEnExistencia', e.target.value)}
          />
          <Input
            label="Entrega de lo que es por encargo"
            hint="Sale en las piezas agotadas que marcaste «se puede encargar». Promete solo lo que ya probaste."
            value={form.entregaPorEncargo}
            onChange={(e) => poner('entregaPorEncargo', e.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Lo que dice</h2>
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Lema"
              hint="La línea pequeña sobre el logo."
              value={form.lema}
              onChange={(e) => poner('lema', e.target.value)}
            />
            <Input
              label="Firma"
              hint="La frase en caligrafía bajo el logo."
              value={form.firma}
              onChange={(e) => poner('firma', e.target.value)}
            />
          </div>
          <Textarea
            label="Presentación"
            rows={3}
            hint="Dos o tres líneas sobre la marca, antes de la colección."
            value={form.presentacion}
            onChange={(e) => poner('presentacion', e.target.value)}
          />
          <div className="grid gap-3 sm:grid-cols-2">
            <Input
              label="Ubicación"
              placeholder="Ciudad, país"
              value={form.ubicacion}
              onChange={(e) => poner('ubicacion', e.target.value)}
            />
            <Input
              label="Despedida"
              hint="La frase en caligrafía con la que cierra la página."
              value={form.cierre}
              onChange={(e) => poner('cierre', e.target.value)}
            />
          </div>
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-3">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">
            La marca, con sus palabras
          </h2>
          <p className="text-xs text-slate-500 dark:text-slate-400">
            Secciones que acompañan a la colección. La que dejes vacía no sale.
          </p>
          <div className="grid gap-3 sm:grid-cols-2">
            <ListaEnRenglones
              label="¿Qué buscas? · tus líneas"
              hint="Una por renglón. Si coincide con una categoría con piezas, la filtra; si no, lleva al WhatsApp."
              valor={form.lineas}
              alCambiar={(l) => poner('lineas', l)}
            />
            <ListaEnRenglones
              label="La cinta · lo que te distingue"
              hint="Frases cortas, una por renglón. Pasan en una cinta."
              valor={form.promesas}
              alCambiar={(l) => poner('promesas', l)}
            />
          </div>
          <Input
            label="Historia · título"
            value={form.historiaTitulo}
            onChange={(e) => poner('historiaTitulo', e.target.value)}
          />
          <Textarea
            label="Historia · texto"
            rows={4}
            hint="Quién eres y para quién haces esto."
            value={form.historiaTexto}
            onChange={(e) => poner('historiaTexto', e.target.value)}
          />
        </CardBody>
      </Card>

      <Card>
        <CardBody className="space-y-4">
          <h2 className="text-sm font-semibold text-slate-800 dark:text-slate-100">Marca</h2>

          <div className="grid gap-4 sm:grid-cols-2">
            <SubirLogo
              cual="emblema"
              titulo="Emblema"
              ayuda="El logo grande de la portada. PNG con fondo transparente."
              actual={form.emblema}
            />
            <SubirLogo
              cual="isotipo"
              titulo="Isotipo"
              ayuda="El símbolo solo: va de marca de agua."
              actual={form.isotipo}
            />
          </div>

          <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-3">
            {ROLES_DE_COLOR.map(({ clave, nombre, uso }) => (
              <label key={clave} className="flex items-center gap-2.5">
                <input
                  type="color"
                  value={form.colores[clave]}
                  onChange={(e) =>
                    poner('colores', { ...form.colores, [clave]: e.target.value.toUpperCase() })
                  }
                  className="h-10 w-10 shrink-0 cursor-pointer rounded-lg border border-surface-border bg-transparent p-0.5"
                />
                <span className="min-w-0">
                  <span className="block text-sm font-medium text-slate-800 dark:text-slate-100">
                    {nombre}{' '}
                    <span className="font-mono text-xs font-normal text-slate-500">
                      {form.colores[clave]}
                    </span>
                  </span>
                  <span className="block truncate text-xs text-slate-500 dark:text-slate-400">
                    {uso}
                  </span>
                </span>
              </label>
            ))}
          </div>

          <div className="grid gap-3 sm:grid-cols-2">
            {ROLES_DE_FUENTE.map(({ clave, nombre, uso }) => (
              <Input
                key={clave}
                label={`Tipografía · ${nombre}`}
                hint={`${uso}. Nombre tal como sale en Google Fonts.`}
                value={form.fuentes[clave]}
                onChange={(e) => poner('fuentes', { ...form.fuentes, [clave]: e.target.value })}
              />
            ))}
          </div>
        </CardBody>
      </Card>

      <div className="sticky bottom-20 z-10 flex justify-end md:bottom-4">
        <Button
          loading={guardar.isPending}
          disabled={!hayCambios}
          onClick={() => guardar.mutate(form)}
          className="shadow-lg"
        >
          {hayCambios ? 'Guardar cambios' : 'Todo guardado'}
        </Button>
      </div>
    </div>
  );
}
